// State + navigation hook for the 4-step indication form.
//
// Responsibilities:
//   - hold a partial IndicationInput
//   - per-step validation via zod
//   - step navigation bound to ?step=1..4 so browser back/forward works
//   - sessionStorage autosave (debounced) + restore within 48h
//   - final submit handoff (Phase 6 will wire up)

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ConstructionType, Deductibles, StateCode } from '@zg/shared';
import { DEFAULT_RATE_CONFIG } from '@zg/shared';
import {
  stepBudgetSchema,
  stepDeductiblesSchema,
  stepProjectInfoSchema,
  stepTermSchema,
} from '../lib/validate';

// --- types -----------------------------------------------------------------

/**
 * Every field in `IndicationInput` can be partially-filled while the user is
 * typing. We keep numbers as `number | undefined` (never empty-string) so the
 * zod schemas' `invalid_type_error` actually fires on required fields.
 */
export interface IndicationDraft {
  namedInsured: string;
  projectAddress: string;
  cityStateZip: string;
  constructionType?: ConstructionType;
  numberOfBuildings?: number;
  numberOfStories?: number;
  squareFootage?: number;
  numberOfUnits?: number;
  state?: StateCode;
  projectDescription?: string;
  hardCosts?: number;
  softCosts?: number;
  lossOfBusinessIncome?: number;
  startDate: string;
  endDate: string;
  deductibles: Deductibles;
}

export type FieldKey = keyof IndicationDraft;
export type DeductibleKey = keyof Deductibles;

export type FormErrors = Partial<Record<string, string>>;

export const STEP_COUNT = 4;
export type StepIndex = 0 | 1 | 2 | 3;

export interface UseIndicationFormResult {
  state: IndicationDraft;
  step: StepIndex;
  errors: FormErrors;
  touched: Record<string, boolean>;
  draftRestored: boolean;
  /** Generic field setter. For deductibles use setDeductible. */
  setField: <K extends FieldKey>(key: K, value: IndicationDraft[K]) => void;
  setDeductible: (key: DeductibleKey, value: number) => void;
  markTouched: (key: string) => void;
  next: () => boolean;
  prev: () => void;
  goTo: (index: number) => void;
  /** Has the current step passed validation? */
  canAdvance: boolean;
  /** Is the state OTHER? If so, form is blocked at step 1. */
  blockedOnOtherState: boolean;
  /**
   * Validates the final step and, on success, marks the form as ready for
   * handoff to the EmailGate. Returns true when validation passed.
   */
  submit: () => boolean;
  pendingSubmit: boolean;
  /** Clear the persisted draft after a successful compute. */
  clearDraft: () => void;
}

// --- defaults + persistence ------------------------------------------------

const STORAGE_KEY = 'zg-indication-draft';
const MAX_DRAFT_AGE_MS = 48 * 60 * 60 * 1000; // 48h

function defaultDraft(): IndicationDraft {
  const { deductibleOptions } = DEFAULT_RATE_CONFIG;
  return {
    namedInsured: '',
    projectAddress: '',
    cityStateZip: '',
    projectDescription: '',
    startDate: '',
    endDate: '',
    deductibles: {
      brAop: deductibleOptions.brAop[0]!,
      brFlood: deductibleOptions.brFlood[0]!,
      brWaterDamage: deductibleOptions.brWaterDamage[0]!,
      oiGl: deductibleOptions.oiGl[0]!,
      pollution: deductibleOptions.pollution[0]!,
    },
  };
}

interface PersistedDraft {
  savedAt: number;
  data: IndicationDraft;
}

function loadPersisted(): IndicationDraft | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedDraft;
    if (!parsed || typeof parsed.savedAt !== 'number') return null;
    if (Date.now() - parsed.savedAt > MAX_DRAFT_AGE_MS) return null;
    // merge over defaults so new fields get sane values
    return { ...defaultDraft(), ...parsed.data, deductibles: { ...defaultDraft().deductibles, ...parsed.data.deductibles } };
  } catch {
    return null;
  }
}

function clearPersisted(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

// --- validation ------------------------------------------------------------

function zodErrors<T>(
  result: { success: true; data: T } | { success: false; error: { issues: Array<{ path: (string | number)[]; message: string }> } },
): FormErrors {
  if (result.success) return {};
  const out: FormErrors = {};
  for (const issue of result.error.issues) {
    const path = issue.path.join('.');
    if (!(path in out)) out[path] = issue.message;
  }
  return out;
}

function validateStep(step: StepIndex, draft: IndicationDraft): FormErrors {
  switch (step) {
    case 0:
      return zodErrors(stepProjectInfoSchema.safeParse(draft));
    case 1:
      return zodErrors(stepBudgetSchema.safeParse(draft));
    case 2:
      return zodErrors(stepTermSchema.safeParse(draft));
    case 3:
      return zodErrors(stepDeductiblesSchema.safeParse({ deductibles: draft.deductibles }));
    default:
      return {};
  }
}

// --- hook ------------------------------------------------------------------

function clampStep(n: number): StepIndex {
  if (n <= 0) return 0;
  if (n >= STEP_COUNT - 1) return (STEP_COUNT - 1) as StepIndex;
  return n as StepIndex;
}

export function useIndicationForm(): UseIndicationFormResult {
  const [searchParams, setSearchParams] = useSearchParams();

  // read initial step from URL (?step=1..4). Default 0.
  const urlStep = clampStep(Number(searchParams.get('step') ?? '1') - 1);

  const [state, setState] = useState<IndicationDraft>(() => loadPersisted() ?? defaultDraft());
  const [draftRestored, setDraftRestored] = useState<boolean>(() => loadPersisted() !== null);
  const [step, setStep] = useState<StepIndex>(urlStep);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [showAllErrors, setShowAllErrors] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(false);

  // sync step <- URL when user navigates browser back/forward
  useEffect(() => {
    const current = clampStep(Number(searchParams.get('step') ?? '1') - 1);
    if (current !== step) setStep(current);
    // intentionally not listing step — this effect only reacts to URL changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // sync URL <- step when `step` changes (replace to keep history sane)
  useEffect(() => {
    const desired = String(step + 1);
    if (searchParams.get('step') !== desired) {
      const next = new URLSearchParams(searchParams);
      next.set('step', desired);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // debounced autosave
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (saveTimer.current !== null) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      try {
        const payload: PersistedDraft = { savedAt: Date.now(), data: state };
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
      } catch {
        // ignore quota / privacy-mode errors
      }
    }, 300);
    return () => {
      if (saveTimer.current !== null) clearTimeout(saveTimer.current);
    };
  }, [state]);

  // auto-dismiss "Draft restored" pill after a short dwell
  useEffect(() => {
    if (!draftRestored) return;
    const t = setTimeout(() => setDraftRestored(false), 6000);
    return () => clearTimeout(t);
  }, [draftRestored]);

  const setField = useCallback(<K extends FieldKey>(key: K, value: IndicationDraft[K]) => {
    setState((prev) => ({ ...prev, [key]: value }));
  }, []);

  const setDeductible = useCallback((key: DeductibleKey, value: number) => {
    setState((prev) => ({ ...prev, deductibles: { ...prev.deductibles, [key]: value } }));
  }, []);

  const markTouched = useCallback((key: string) => {
    setTouched((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
  }, []);

  // current-step errors, regardless of whether to show them
  const currentErrors = useMemo(() => validateStep(step, state), [step, state]);

  // Errors to show in the UI: touched fields, or everything if user clicked Next.
  const errors = useMemo<FormErrors>(() => {
    if (showAllErrors) return currentErrors;
    const out: FormErrors = {};
    for (const [k, v] of Object.entries(currentErrors)) {
      if (touched[k]) out[k] = v;
    }
    return out;
  }, [currentErrors, touched, showAllErrors]);

  const canAdvance = Object.keys(currentErrors).length === 0;

  const blockedOnOtherState = state.state === 'OTHER';

  const next = useCallback((): boolean => {
    if (blockedOnOtherState) return false;
    const errs = validateStep(step, state);
    if (Object.keys(errs).length > 0) {
      setShowAllErrors(true);
      // mark every errored field as touched so styling persists
      setTouched((prev) => {
        const copy = { ...prev };
        for (const k of Object.keys(errs)) copy[k] = true;
        return copy;
      });
      // scroll to first invalid field
      if (typeof document !== 'undefined') {
        queueMicrotask(() => {
          const firstKey = Object.keys(errs)[0];
          if (firstKey === undefined) return;
          const el = document.querySelector<HTMLElement>(`[data-field="${firstKey}"]`);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            const focusable = el.querySelector<HTMLElement>('input, select, textarea');
            focusable?.focus({ preventScroll: true });
          }
        });
      }
      return false;
    }
    setShowAllErrors(false);
    if (step < STEP_COUNT - 1) {
      setStep((s) => clampStep(s + 1));
    }
    return true;
  }, [blockedOnOtherState, step, state]);

  const prev = useCallback(() => {
    setShowAllErrors(false);
    setStep((s) => clampStep(s - 1));
  }, []);

  const goTo = useCallback((index: number) => {
    setShowAllErrors(false);
    setStep(clampStep(index));
  }, []);

  const submit = useCallback((): boolean => {
    const errs = validateStep(step, state);
    if (Object.keys(errs).length > 0) {
      setShowAllErrors(true);
      setTouched((prev) => {
        const copy = { ...prev };
        for (const k of Object.keys(errs)) copy[k] = true;
        return copy;
      });
      return false;
    }
    setPendingSubmit(true);
    return true;
  }, [state, step]);

  const clearDraft = useCallback(() => {
    clearPersisted();
  }, []);

  return {
    state,
    step,
    errors,
    touched,
    draftRestored,
    setField,
    setDeductible,
    markTouched,
    next,
    prev,
    goTo,
    canAdvance,
    blockedOnOtherState,
    submit,
    pendingSubmit,
    clearDraft,
  };
}
